import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { Suspense } from "react";
import { SheetSkeleton } from "@/components/skeletons/sheet-skeleton";
import { RenderStatusBadge } from "@/components/table/cells/RenderStatusBadge";
import { FormStepSeparator } from "@/components/ui/form-step-seperator";
import {
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { BasicTooltip } from "@/components/ui/tooltip";
import { UserAvatar } from "@/components/user-avatar";
import { UploadedFilesView } from "./-components/uploaded-files-view";

export const Route = createFileRoute("/_authed/_org/requests/$id")({
  component: RouteComponent,
  loader: ({ context: { queryClient, trpc }, params: { id } }) => {
    queryClient.ensureQueryData(
      trpc.request.get.queryOptions({
        requestId: id,
      }),
    );
  },
});

function RouteComponent() {
  const { id } = Route.useParams();
  const { trpc } = Route.useRouteContext();

  const { data: request } = useSuspenseQuery(
    trpc.request.get.queryOptions({
      requestId: id,
    }),
  );

  return (
    <div className="flex h-full flex-col">
      <SheetHeader className={"px-6 py-6"}>
        <SheetTitle className="flex items-center gap-4">
          Request Details{" "}
          <BasicTooltip
            tooltip={request.statusReason}
            disabled={!request.statusReason}
          >
            <button type="button" className="flex items-center outline-none">
              <RenderStatusBadge className="shadow" status={request.status} />
            </button>
          </BasicTooltip>
        </SheetTitle>
        <SheetDescription className="flex items-center gap-2">
          Created by <UserAvatar userId={request.forUserId} />
        </SheetDescription>

        <div className="flex flex-col gap-2">
          <div className="flex flex-col">
            <FormStepSeparator description="Reason" />
            <p className="text-neutral-600 text-sm">{request.reason}</p>
          </div>

          {Boolean(request.files?.length) && (
            <div className="flex flex-col">
              <FormStepSeparator description="Uploaded Files" />
              <UploadedFilesView files={request.files} />
            </div>
          )}
        </div>
      </SheetHeader>

      <Suspense
        fallback={
          <div className="px-6">
            <SheetSkeleton />
          </div>
        }
      >
        <Outlet />
      </Suspense>
    </div>
  );
}
